import { PAYEE_FORM_MAX_LENGTH } from '@/components/PayeeDetailForm/constants';
import { InputBaseType } from '@/components/ReportForm/InputBase/types';
import { useTranslation } from '@/i18n';
import {
  FormTextArea,
  FormTextAreaProps,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames/bind';
import { memo, useMemo } from 'react';
import { FieldValues, useFormState, useWatch } from 'react-hook-form';
import styles from './ContactAddressDetail.module.scss';

const cx = classNames.bind(styles);

export type Props<TFieldValues extends FieldValues> = Omit<
  InputBaseType<TFieldValues>,
  'label' | 'tooltip' | 'caption' | 'hidden'
> &
  FormTextAreaProps<TFieldValues> & {
    maxLength?: number;
  };

const InnerContactAddressDetail = <TFieldValues extends FieldValues>({
  control,
  name,
  inputId,
  required = false,
  maxLength = PAYEE_FORM_MAX_LENGTH.CONTACT_ADDRESS,
  ...rest
}: Props<TFieldValues>) => {
  const { t } = useTranslation();

  const { errors } = useFormState({ control });
  const fieldError = errors[name];

  const addressValue = useWatch({
    control,
    name,
  });

  const addressLength = useMemo(() => {
    if (typeof addressValue === 'string') {
      return addressValue.length;
    }
    return 0;
  }, [addressValue]);

  const rules = useMemo(
    () => ({
      required: required ? t('required') : false,
      maxLength: {
        value: maxLength,
        message: t('ap_payee_address_max_error', {
          maxLength: maxLength,
        }),
      },
    }),
    [required, t, maxLength]
  );

  const labelProps = useMemo(
    () => ({
      text: t('ap_payee_address'),
    }),
    [t]
  );

  const textAreaProps = useMemo(
    () => ({
      placeholder: t('ap_payee_address_placeholder'),
      autoSize: { minRows: 1 },
    }),
    [t]
  );

  return (
    <div className={cx('container')}>
      <FormTextArea<TFieldValues>
        label={labelProps}
        control={control}
        name={name}
        inputId={inputId}
        rules={rules}
        textAreaProps={textAreaProps}
        {...rest}
      />
      <div className={cx('counter', { error: !!fieldError })}>
        {addressLength}/{maxLength}
      </div>
    </div>
  );
};

InnerContactAddressDetail.displayName = 'InnerContactAddressDetail';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ContactAddressDetail = memo((props) => (
  <InnerContactAddressDetail {...props} />
)) as typeof InnerContactAddressDetail;
ContactAddressDetail.displayName = 'ContactAddressDetail';
